/**
 * TwinChatDock (iter1066) — сгъваем разговор в долната част на всяка
 * страница на близнак, плюс бутон „Попитайте мрежата" на подходящите места.
 * Близнакът се разпознава по адреса чрез единния списък в lib/twinCatalog.
 */
import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { MessageCircle, X } from "lucide-react";
import TwinChatPanel from "@/components/twin_chat/TwinChatPanel";
import { TWIN_CATALOG, twinLabel } from "@/lib/twinCatalog";

// Страници, на които разговорът вече стои вграден
const SKIP = ["/twins/network"];

// „Лабораторни изследвания" е единният вход за резултати, не тема за
// отделен разговор — там питаме цялата мрежа.
const NO_FOCUS = ["lab"];

// Извън страниците на близнаците разговорът също е на място
const EXTRA_PATHS = [
  "/dashboard",
  "/dashboard/marker-map",
  "/dashboard/lab-twin",
  "/dashboard/analyses",
  "/health-intelligence",
];

export default function TwinChatDock() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  // Когато въпросът идва от страница със симулатор на чинията, разговорът
  // получава днешното хранене като водещ контекст.
  const [focusScope, setFocusScope] = useState(null);

  const twin = useMemo(() => {
    const entry = Object.entries(TWIN_CATALOG)
      .filter(([, v]) => v.route && v.route !== "/dashboard")
      .sort((a, b) => b[1].route.length - a[1].route.length)
      .find(([, v]) => pathname === v.route || pathname.startsWith(`${v.route}/`));
    if (!entry || NO_FOCUS.includes(entry[0])) return null;
    return entry[0];
  }, [pathname]);

  const allowed = useMemo(() => {
    if (SKIP.some((p) => pathname.startsWith(p))) return false;
    if (twin) return true;
    return EXTRA_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  }, [pathname, twin]);

  if (!user || !allowed) return null;

  const label = twin ? `Попитайте за „${twinLabel(twin)}“` : "Попитайте мрежата";

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => {
          const onPlate = !!document.querySelector(
            '[data-testid="plate-simulator-card"], #plate-simulator-anchor',
          );
          setFocusScope(onPlate ? "plate" : null);
          setOpen(true);
        }}
        data-testid="twin-chat-dock-btn"
        aria-label={label}
        title={label}
        className={`fixed z-[9995] bottom-[calc(11rem+env(safe-area-inset-bottom,0px))] sm:bottom-24 right-4 sm:right-6 inline-flex items-center justify-center gap-0 sm:gap-2
          h-12 w-12 sm:h-auto sm:w-auto sm:pl-3 sm:pr-4 sm:py-2.5
          rounded-full text-xs font-bold shadow-lg transition-transform
          hover:scale-[1.03] ${isDark
            ? "bg-indigo-600 text-white shadow-indigo-900/40"
            : "bg-indigo-600 text-white shadow-indigo-500/30"}`}
      >
        <MessageCircle className="w-5 h-5 sm:w-4 sm:h-4" />
        <span className="hidden sm:inline">{label}</span>
      </button>
    );
  }

  return (
    <div
      className={`fixed z-[9998] bottom-[calc(7rem+env(safe-area-inset-bottom,0px))] right-2 left-2 sm:bottom-24 sm:right-6 sm:left-auto sm:w-[420px]
        max-h-[70vh] overflow-y-auto overflow-x-hidden rounded-2xl border p-4 shadow-2xl backdrop-blur-md ${
          isDark ? "bg-slate-900/95 border-slate-700" : "bg-white/97 border-slate-200"}`}
      data-testid="twin-chat-dock"
    >
      <button
        type="button"
        onClick={() => setOpen(false)}
        data-testid="twin-chat-dock-close"
        className="absolute top-2.5 right-2.5 opacity-50 hover:opacity-100"
      >
        <X className="w-4 h-4" />
      </button>
      <TwinChatPanel twin={twin} embedded focusScope={focusScope} />
    </div>
  );
}
